import React, { useState } from 'react';
import { X, Plus, Repeat, Trash2 } from 'lucide-react';
import { toRuns } from './GroupDatesSelector';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parse = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const pretty = (s) => parse(s).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
const inputCls = 'border rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-200';
const expand = (from, to) => { const out = []; if (!from || !to || to < from) return out; for (let d = parse(from); iso(d) <= to; d.setDate(d.getDate() + 1)) out.push(iso(d)); return out; };

// Several date ranges (From/To rows) plus a "repeat weekly" helper; emits one sorted list of days.
export const MultiDatePicker = ({ dates, onChange, maxDaysAhead = 90, allowPast = false, max = 60, testPrefix = 'md' }) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const minStr = allowPast ? undefined : iso(today);
  const maxStr = iso(new Date(today.getTime() + maxDaysAhead * 86400000));
  const [rows, setRows] = useState(() => {
    const runs = toRuns(dates).filter((r) => r.length > 1).map((r) => ({ from: r[0], to: r[r.length - 1] }));
    return runs.length ? runs : [{ from: '', to: '' }];
  });
  const [weekday, setWeekday] = useState(String(today.getDay() === 0 ? 1 : today.getDay()));
  const [from, setFrom] = useState(iso(today));
  const [until, setUntil] = useState('');
  const [msg, setMsg] = useState('');

  const inWindow = (d) => (!minStr || d >= minStr) && d <= maxStr;
  const rowDays = (rs) => rs.flatMap((r) => expand(r.from, r.to));
  const extras = dates.filter((d) => !rowDays(rows).includes(d));
  const emit = (list) => {
    const ok = list.filter(inWindow);
    const merged = [...new Set(ok)].sort();
    setMsg(ok.length < list.length ? `Days outside the allowed window (up to ${maxStr}) were left out.` : merged.length > max ? `Up to ${max} days per request — the rest were left out.` : '');
    onChange(merged.slice(0, max));
  };
  const setRow = (i, patch) => {
    const next = rows.map((r, n) => (n === i ? { ...r, ...patch, ...(patch.from && (!r.to || r.to < patch.from) ? { to: patch.from } : {}) } : r));
    setRows(next);
    emit([...rowDays(next), ...extras]);
  };
  const removeRow = (i) => {
    const next = rows.length > 1 ? rows.filter((_, n) => n !== i) : [{ from: '', to: '' }];
    setRows(next);
    emit([...rowDays(next), ...extras]);
  };
  const addWeekly = () => {
    if (!from || !until) return;
    emit([...dates, ...expand(from, until).filter((d) => parse(d).getDay() === Number(weekday))]);
  };
  const clear = () => { setRows([{ from: '', to: '' }]); setMsg(''); onChange([]); };

  return (
    <div className="space-y-3" data-testid={`${testPrefix}-picker`}>
      <div className="rounded-lg border bg-gray-50/60 p-2.5 space-y-2" data-testid={`${testPrefix}-ranges`}>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end" data-testid={`${testPrefix}-range-${i}`}>
            <label className="text-[11px] text-gray-500">From
              <input type="date" className={`${inputCls} w-full mt-0.5`} min={minStr} max={maxStr} value={r.from} onChange={(e) => setRow(i, { from: e.target.value })} data-testid={`${testPrefix}-range-from-${i}`} /></label>
            <label className="text-[11px] text-gray-500">To
              <input type="date" className={`${inputCls} w-full mt-0.5`} min={r.from || minStr} max={maxStr} value={r.to} onChange={(e) => setRow(i, { to: e.target.value })} data-testid={`${testPrefix}-range-to-${i}`} /></label>
            <button type="button" onClick={() => removeRow(i)} className="h-9 w-8 flex items-center justify-center text-gray-400 hover:text-red-600" title="Remove range" data-testid={`${testPrefix}-range-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            {r.from && r.to && <p className="col-span-3 text-[11px] text-sky-700 -mt-1">{expand(r.from, r.to).filter((d) => dates.includes(d)).length} day(s)</p>}
          </div>
        ))}
        <button type="button" onClick={() => setRows([...rows, { from: '', to: '' }])} className="w-full flex items-center justify-center gap-1 py-1.5 rounded-md border bg-white text-sm text-gray-700 hover:bg-gray-50 transition-colors" data-testid={`${testPrefix}-add-range`}>
          <Plus className="h-3.5 w-3.5" />Add another date range
        </button>
        <p className="text-[10px] text-gray-400">A single day? Use the same date in From and To.</p>
      </div>
      <div className="rounded-lg border border-dashed border-sky-200 bg-sky-50/40 p-2.5 space-y-2" data-testid={`${testPrefix}-weekly`}>
        <p className="text-xs font-medium text-sky-800 flex items-center gap-1"><Repeat className="h-3.5 w-3.5" />Repeat weekly</p>
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
          Every
          <select className={inputCls} value={weekday} onChange={(e) => setWeekday(e.target.value)} data-testid={`${testPrefix}-weekday`}>
            {WEEKDAYS.map((w, i) => <option key={w} value={i}>{w}</option>)}
          </select>
          from <input type="date" className={inputCls} min={minStr} max={maxStr} value={from} onChange={(e) => setFrom(e.target.value)} data-testid={`${testPrefix}-weekly-from`} />
          until <input type="date" className={inputCls} min={from || minStr} max={maxStr} value={until} onChange={(e) => setUntil(e.target.value)} data-testid={`${testPrefix}-weekly-until`} />
          <button type="button" disabled={!from || !until} onClick={addWeekly} className="px-2.5 py-1.5 rounded-md border border-sky-300 text-sky-700 bg-white hover:bg-sky-50 disabled:opacity-40 transition-colors" data-testid={`${testPrefix}-weekly-add`}>Add dates</button>
        </div>
      </div>
      {msg && <p className="text-[11px] text-amber-700" data-testid={`${testPrefix}-msg`}>{msg}</p>}
      {extras.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testPrefix}-chips`}>
          {extras.map((d) => (
            <span key={d} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-sky-100 text-sky-800 border border-sky-200" data-testid={`${testPrefix}-chip-${d}`}>
              {pretty(d)}
              <button type="button" onClick={() => onChange(dates.filter((x) => x !== d))} className="hover:text-red-600" data-testid={`${testPrefix}-remove-${d}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between text-xs">
        <span className={dates.length ? 'font-medium text-sky-700' : 'text-gray-400'} data-testid={`${testPrefix}-count`}>{dates.length ? `${dates.length} day${dates.length !== 1 ? 's' : ''} selected` : 'No dates picked yet'}</span>
        {dates.length > 0 && <button type="button" onClick={clear} className="text-gray-500 hover:text-red-600 underline" data-testid={`${testPrefix}-clear`}>Clear all</button>}
      </div>
    </div>
  );
};
